import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Button,
  ButtonOwnProps,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import BusinessIcon from "@mui/icons-material/Business";
import AttributionIcon from "@mui/icons-material/Attribution";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { visuallyHidden } from "@mui/utils";
import { Box, TableContainer, TablePagination, TableSortLabel } from "@mui/material";

import conf from "../../conf";
import format from "../utils/Role";
import Spinner from "../utils/Spinner";
import InfoChip from "../utils/InfoChip";
import ManageUser from "./ManageUser";
import EmptyTableRow from "../utils/Empty";
import usePagination from "../../hooks/pagination";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import LinearIndeterminate from "../utils/LinearLoader";
import { Idle, Offline, Online } from "../utils/Status";
import { ROLES, useAuth, User } from "../auth/AuthProvider";
import { EditAssignment, ActionType } from "../users/Radio";
import {
  Organization,
  useConfgSysOrganizationDlmVerify,
  useConfgSysOrganizationEditSettings,
  useConfgSysOrganizationRemove,
  useConfgSysOrganizationUsersAdd,
  useConfgSysOrganizationUsersRemove,
  useConfgUsers
} from "../../hooks/confg/base";

const domainIcon = (
  <InputAdornment position="end">
    <InfoChip size="medium" info="This attribute is used to specify identity provider validity." />
  </InputAdornment>
);

const UserRow = (props: {
  user: User;
  action: ActionType;
  organization: Organization;
  handleClick: (e: React.MouseEvent<unknown>, id: string) => void;
  isItemSelected: boolean;
}) => {
  const { action, user, isItemSelected, organization, handleClick } = props;
  const { id, email, lastActive } = user;

  const org = user.organizations.find((org) => org.id === organization.id);
  const role = org ? org.role : "--";
  const status = org ? org.status : "--";
  const isAssigned = org ? true : false;

  const { user: self } = useAuth();

  const isDisabled = (action === "assign" && isAssigned) || (action === "unassign" && !isAssigned) || email === self.email;

  const EmailIcon =
    email === self.email ? (
      <AttributionIcon fontSize="small" color="success" />
    ) : user.session.status === "active" ? (
      Online
    ) : (
      Offline
    );

  return (
    <TableRow
      key={id}
      hover={!isDisabled}
      onClick={isDisabled ? () => {} : (e) => handleClick(e, id)}
      tabIndex={-1}
      selected={isItemSelected}
      aria-checked={isItemSelected}
      sx={conf.STYLE.LAST_CHILD_NO_BORDER}
    >
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          checked={isItemSelected}
          onClick={(e) => handleClick(e, id)}
          disabled={isDisabled}
          inputProps={{ "aria-labelledby": id }}
        />
      </TableCell>

      <TableCell component="th" scope="row">
        <Chip size="small" label={email} icon={EmailIcon} />
      </TableCell>

      <TableCell align="right">{status}</TableCell>

      <TableCell align="right">{format(role || "--")}</TableCell>

      <TableCell align="right">{moment(lastActive).fromNow()}</TableCell>

      <TableCell align="right">
        {isAssigned ? <CheckIcon fontSize="small" color="success" /> : <CloseIcon fontSize="small" color="error" />}
      </TableCell>

      <TableCell align="right">
        <ManageUser iconOnly size="small" user={user} disabled={!isAssigned} organization={organization} />
      </TableCell>
    </TableRow>
  );
};

type UsersProps = {
  action: ActionType;
  selected: readonly string[];
  organization: Organization;
  setSelected: Dispatch<SetStateAction<readonly string[]>>;
  handleClick: (e: React.MouseEvent<unknown>, id: string) => void;
};

const Users = (props: UsersProps) => {
  const { organization, action, selected, setSelected, handleClick } = props;

  const paginate = usePagination({ defaultSort: "email" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const users = useConfgUsers(body);

  return (
    <Paper elevation={3}>
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />

              {conf.COLUMNS.USERS.MGMT.map((column) => (
                <TableCell key={column.label} sortDirection={order} align={column.isHeader ? "left" : "right"}>
                  <TableSortLabel
                    active={sort === column.key}
                    disabled={column.disabled}
                    direction={sort === column.key ? order : "asc"}
                    onClick={(event: React.MouseEvent<unknown>) => handleSort(event, column.key)}
                  >
                    <b>{column.label}</b>
                    {sort === column.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}

              <TableCell align="right">
                <AssignmentIndIcon fontSize="inherit" />
              </TableCell>
            </TableRow>
          </TableHead>

          {users.isLoading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearIndeterminate />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {users?.data?.data.length === 0 ? (
                <EmptyTableRow type="users" colSpan={8} />
              ) : (
                users?.data?.data?.map((user: User) => {
                  const isItemSelected = selected.includes(user.id);

                  return (
                    <UserRow
                      key={user.id}
                      user={user}
                      action={action}
                      organization={organization}
                      handleClick={handleClick}
                      isItemSelected={isItemSelected}
                    />
                  );
                })
              )}
            </TableBody>
          )}
        </Table>

        <TablePagination
          component="div"
          page={page}
          count={users?.data?.meta.total || 0}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onRowsPerPageChange={handleChangeLimit}
        />
      </TableContainer>
    </Paper>
  );
};

type ManageViewProps = {
  open: boolean;
  onClose: () => void;
  organization: Organization;
};

const ManageView = (props: ManageViewProps) => {
  const { open, organization, onClose } = props;

  const [role, setRole] = useState<ROLES>();
  const [action, setAction] = useState<ActionType>("assign");
  const [comment, setComment] = useState<string>("");
  const [selected, setSelected] = useState<readonly string[]>([]);

  const [domain, setDomain] = useState<string>(organization.domain);
  const [acronym, setAcronym] = useState<string>(organization.acronym);
  const [urlPublic, setUrlPublic] = useState<string>(organization.url?.public);
  const [urlPrivate, setUrlPrivate] = useState<string>(organization.url?.private);
  const [description, setDescription] = useState<string>(organization.description);
  const [dlmToken, setDlmToken] = useState<string>(organization.dlm?.token);
  const [dlmGroupId, setDlmGroupId] = useState<string>(organization.dlm?.groupId);
  const [dlmPushUrl, setDlmPushUrl] = useState<string>(organization.dlm?.pushUrl);
  const [dlmVerifyUrl, setDlmVerifyUrl] = useState<string>(organization.dlm?.verifyUrl);

  const add = useConfgSysOrganizationUsersAdd();
  const remove = useConfgSysOrganizationUsersRemove();
  const removeOrg = useConfgSysOrganizationRemove();

  const verify = useConfgSysOrganizationDlmVerify();
  const settings = useConfgSysOrganizationEditSettings();

  const handleVerify = () => verify.mutate({ id: organization.id });

  const handleSettingsEdit = () =>
    settings.mutate({
      id: organization.id,
      domain,
      acronym,
      description,
      urlPublic,
      urlPrivate,
      dlm: {
        token: dlmToken,
        groupId: dlmGroupId,
        pushUrl: dlmPushUrl,
        verifyUrl: dlmVerifyUrl
      }
    });

  const handleSettingsReset = () => {
    setDomain(organization.domain);
    setAcronym(organization.acronym);
    setUrlPublic(organization.url.public);
    setUrlPrivate(organization.url.private);
    setDescription(organization.description);
    setDlmToken(organization.dlm.token);
    setDlmGroupId(organization.dlm.groupId);
    setDlmPushUrl(organization.dlm.pushUrl);
    setDlmVerifyUrl(organization.dlm.verifyUrl);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRole((e.target as HTMLInputElement).value as ROLES);
  };

  const handleClick = (e: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleSubmit = () => {
    if (action === "assign") {
      add.mutate({
        role,
        organizationId: organization.id,
        userIds: selected,
        comment
      });
    } else if (action === "unassign") {
      remove.mutate({
        organizationId: organization.id,
        userIds: selected,
        comment
      });
    }
  };

  const handleRemoveOrg = () => removeOrg.mutate({ id: organization.id });

  const warning = (
    <>
      You are about to remove the following organization: <b>{organization.name.toUpperCase()}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  useEffect(() => {
    setRole(undefined);
    setSelected([]);
  }, [action]);

  useEffect(() => {
    if (add.isSuccess) {
      setComment("");
      setSelected([]);
    }
  }, [add.isSuccess]);

  useEffect(() => {
    if (remove.isSuccess) {
      setComment("");
      setSelected([]);
    }
  }, [remove.isSuccess]);

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <DialogTitle>
          <Chip color="secondary" icon={<BusinessIcon />} label={<b>{organization.name}</b>} />
        </DialogTitle>

        <Button size="small" color="inherit" onClick={onClose}>
          <CloseIcon />
        </Button>
      </Stack>

      <Divider />

      <DialogContent>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography gutterBottom variant="h6" component="div">
            General
          </Typography>

          <Button key={"organization-settings-reset"} size="small" color="inherit" onClick={handleSettingsReset}>
            Reset
          </Button>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <DialogContentText variant="caption">
              To manage general settings, please edit the following fields then submit. Changes will not take effect until
              submitted.
            </DialogContentText>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-domain"
              name="organization-domain"
              label="Domain"
              value={domain}
              onChange={(e: any) => setDomain(e.target.value)}
              InputProps={{ endAdornment: domainIcon }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-acronym"
              name="organization-acronym"
              label="Acronym"
              value={acronym}
              onChange={(e: any) => setAcronym(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-public-url"
              name="organization-public-url"
              label="Public URL"
              value={urlPublic}
              onChange={(e: any) => setUrlPublic(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-private-url"
              name="organization-private-url"
              label="Private URL"
              value={urlPrivate}
              onChange={(e: any) => setUrlPrivate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              id="organization-description"
              name="organization-description"
              label="Description"
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="dlm-push-url"
              label="DLM push url"
              value={dlmPushUrl}
              onChange={(e) => setDlmPushUrl(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="dlm-verify-url"
              name="dlm-verify-url"
              label="DLM verify url"
              value={dlmVerifyUrl}
              onChange={(e) => setDlmVerifyUrl(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              id="dlm-group-id"
              label="DLM group id"
              value={dlmGroupId}
              onChange={(e) => setDlmGroupId(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              multiline
              id="dlm-push-token"
              rows={5}
              label="DLM token"
              value={dlmToken}
              onChange={(e) => setDlmToken(e.target.value)}
            />
          </Grid>
        </Grid>

        <DialogActions>
          <Button type="submit" variant="contained" onClick={handleSettingsEdit}>
            {settings.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Save"}
          </Button>
        </DialogActions>

        <Typography gutterBottom variant="h6" component="div">
          DLM
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
              <DialogContentText variant="caption">
                To manage DLM settings, please edit the above fields then submit. Changes will not take effect until
                submitted.
              </DialogContentText>

              {verify.isSuccess ? (
                <Chip size="small" label={"online"} icon={Online} />
              ) : verify.isError ? (
                <Chip size="small" label={"offline"} icon={Offline} />
              ) : (
                <Chip size="small" label={"unknown"} icon={Idle} />
              )}
            </Stack>
          </Grid>

          <Grid item sm={12}>
            <Paper
              variant="outlined"
              elevation={3}
              sx={{
                p: 1,
                opacity: 1,
                textAlign: "left"
              }}
            >
              {verify.status === "idle" && <Typography variant="caption">Click 'Verify' to view current status.</Typography>}

              {verify.status == "success" && (
                <Typography variant="caption" color="success">
                  <i>{JSON.stringify(verify.data.data)}</i>
                </Typography>
              )}

              {verify.status == "error" && (
                <Typography variant="caption" color={"error"}>
                  <i>{verify.error.response?.data.error}</i>
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <DialogActions>
          <Button color="inherit" onClick={() => verify.reset()}>
            {verify.isPending ? "Cancel" : "Reset"}
          </Button>

          {dlmToken !== organization.dlm?.token ||
          dlmGroupId !== organization.dlm?.groupId ||
          dlmPushUrl !== organization.dlm?.pushUrl ||
          dlmVerifyUrl !== organization.dlm?.verifyUrl ? (
            <Tooltip
              title={`Please save DLM settings before attempting Verify. Changes will not take effect until submitted.`}
              placement="left"
            >
              <span style={{ display: "inline-block" }}>
                <Button variant="contained" disabled>
                  Verify
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button variant="contained" onClick={handleVerify}>
              {verify.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Verify"}
            </Button>
          )}
        </DialogActions>

        <Typography gutterBottom variant="h6" component="div">
          Users
        </Typography>

        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
          <DialogContentText variant="caption">
            To manage organization please chose action type (right), then select users below. If adding users, please
            additionally select role from below table. Changes will not take effect until submitted.
          </DialogContentText>

          <EditAssignment action={action} setAction={setAction} />
        </Stack>
        <Users
          action={action}
          selected={selected}
          organization={organization}
          setSelected={setSelected}
          handleClick={handleClick}
        />
        <TextField
          fullWidth
          id="comment"
          type="comment"
          label="Note (optional)"
          margin="dense"
          variant="standard"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        {action === "assign" ? (
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <RadioGroup row value={role} onChange={handleChange}>
              {conf.ROLES.map((role) => (
                <FormControlLabel
                  key={role}
                  value={role}
                  label={format(role)}
                  control={<Radio />}
                  disabled={selected.length === 0}
                />
              ))}
            </RadioGroup>

            <Button type="submit" variant="contained" onClick={handleSubmit} disabled={selected.length === 0 || !role}>
              {add.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Submit"}
            </Button>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button type="submit" variant="contained" onClick={handleSubmit} disabled={selected.length === 0}>
              {remove.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Submit"}
            </Button>
          </DialogActions>
        )}
        <ButtonWithConfirm
          fullWidth
          type="Remove"
          label={"Remove Organization"}
          color="error"
          variant="outlined"
          status={removeOrg.status}
          message={warning}
          onConfirm={handleRemoveOrg}
        />
      </DialogContent>
    </Dialog>
  );
};

const ManageOrg = ({
  organization,
  size = "medium",
  disabled = false,
  fullWidth = false
}: {
  organization: Organization;
  size?: ButtonOwnProps["size"];
  detail?: any;
  disabled?: boolean;
  fullWidth?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const Manage = (
    <Button size={size} type="submit" variant="contained" onClick={handleOpen} disabled={disabled} fullWidth={fullWidth}>
      Manage
    </Button>
  );

  return (
    <div>
      {organization.online ? (
        <Tooltip title={`Click to manage organization`} placement="left">
          {Manage}
        </Tooltip>
      ) : (
        Manage
      )}

      <ManageView open={open} onClose={handleClose} organization={organization} />
    </div>
  );
};

export default ManageOrg;
